import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Cookies from "js-cookie";

const inputStyle = {
  width: "100%",
  backgroundColor: "#fff",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const focusHandlers = {
  onFocus: (e) => {
    e.target.style.borderColor = "#ff5100";
    e.target.style.boxShadow = "0 0 0 3px rgba(255,81,0,0.1)";
  },
  onBlur: (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.boxShadow = "none";
  },
};

const TYPE_OPTIONS = [
  {
    value: "personalized",
    label: "Personalized",
    icon: "fa-user",
    sub: "Show your chosen name to others",
  },
  {
    value: "anonymous",
    label: "Anonymous",
    icon: "fa-user-secret",
    sub: "Hide behind a generated alias",
  },
];

const ChangeDisplayNameModal = ({
  domain,
  show,
  handleClose,
  userData,
  saveDisplayName,
}) => {
  const userId = userData[0];
  const userType = userData[1];
  const userName = userData[2];
  const userEmail = userData[3];
  const userPassword = userData[4];
  const userOrg = userData[5];
  const userPermissions = userData[6];
  const userAnonymous = userData[7];

  const [userDisplayName, setUserDisplayName] = useState(undefined);
  const [userAnonymousName, setUserAnonymousName] = useState(undefined);
  const [displayNameType, setDisplayNameType] = useState(null);

  useEffect(() => {
    if (userId !== undefined) {
      setUserAnonymousName("Bleeper#" + userId.slice(-6));
    }
    setDisplayNameType(userAnonymous === "true" ? "anonymous" : "personalized");
    setUserDisplayName(userName);
  }, [userId]);

  const setDisplayName = () => {
    const anonymous = displayNameType === "anonymous";

    fetch(`/api/users/displayname/${userId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username: userDisplayName, anonymous }),
      redirect: "follow",
    })
      .then((response) => response.text())
      .then(() => {
        Cookies.set("userAnonymous", anonymous);
        Cookies.set("userName", userDisplayName);
        saveDisplayName();
      })
      .catch((error) => console.log("error", error));
  };

  const isPersonalized = displayNameType === "personalized";
  const canSave = isPersonalized ? !!userDisplayName?.trim() : true;

  return (
    <>
      <style>{`
        .change-displayname-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .change-displayname-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .change-displayname-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .change-displayname-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        dialogClassName="change-displayname-modal"
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34, height: 34, borderRadius: 9,
                background: "rgba(255,81,0,0.08)",
                border: "1.5px solid rgba(255,81,0,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <i className="fas fa-id-badge" style={{ color: "#ff5100", fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>Display Name</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>
                Choose how you appear to others
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16, lineHeight: 1.6 }}>
            Your display name is how you appear publicly on your Bleeps, comments, and other activity.
          </div>

          {/* Type selection cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {TYPE_OPTIONS.map((opt) => {
              const selected = displayNameType === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDisplayNameType(opt.value)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "10px 14px", borderRadius: 10, textAlign: "left",
                    border: `1.5px solid ${selected ? "#ff5100" : "#e5e7eb"}`,
                    background: selected ? "rgba(255,81,0,0.06)" : "#fff",
                    cursor: "pointer",
                    transition: "border-color 0.15s, background 0.15s",
                  }}
                >
                  <div
                    style={{
                      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                      background: selected ? "rgba(255,81,0,0.12)" : "#f3f4f6",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <i className={`fas ${opt.icon}`} style={{ fontSize: 13, color: selected ? "#ff5100" : "#9ca3af" }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: selected ? "#ff5100" : "#111827" }}>
                      {opt.label}
                    </div>
                    <div style={{ fontSize: 11, color: selected ? "#f97316" : "#9ca3af", marginTop: 1 }}>
                      {opt.sub}
                    </div>
                  </div>
                  {selected && (
                    <i className="fas fa-check-circle" style={{ color: "#ff5100", fontSize: 16 }} />
                  )}
                </button>
              );
            })}
          </div>

          {/* Personalized input */}
          {isPersonalized && (
            <div style={{ marginTop: 16 }}>
              <div style={{ color: "#6b7280", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>
                Display Name <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>
              </div>
              <input
                type="text"
                placeholder="Enter your display name"
                value={userDisplayName || ""}
                onChange={(e) => setUserDisplayName(e.target.value)}
                style={inputStyle}
                {...focusHandlers}
              />
            </div>
          )}

          {/* Anonymous preview */}
          {displayNameType === "anonymous" && (
            <div
              style={{
                marginTop: 16, background: "#f9fafb",
                border: "1.5px solid #f3f4f6", borderRadius: 10,
                padding: "12px 16px", display: "flex", alignItems: "center", gap: 12,
              }}
            >
              <div
                style={{
                  width: 36, height: 36, borderRadius: 9, flexShrink: 0,
                  background: "rgba(255,81,0,0.08)", border: "1.5px solid rgba(255,81,0,0.15)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <i className="fas fa-user-secret" style={{ color: "#ff5100", fontSize: 14 }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Your alias
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: "#111827", letterSpacing: 4, marginTop: 2 }}>
                  {userAnonymousName}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            onClick={setDisplayName}
            disabled={!canSave}
            style={{
              width: "100%", padding: "11px 0", borderRadius: 10, border: "none",
              background: canSave ? "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)" : "#e5e7eb",
              color: canSave ? "#fff" : "#9ca3af",
              fontWeight: 700, fontSize: 15,
              cursor: canSave ? "pointer" : "not-allowed",
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 9, transition: "background 0.15s",
            }}
          >
            Save Changes
            <i className="fas fa-check" style={{ fontSize: 13 }} />
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChangeDisplayNameModal;
